// Journals (curated collections): named notebooks that gather entries a person has already written.
//
// A collection is a membership relation. Nothing here writes to an entry, and removing an entry from
// a journal is a DELETE against the join, never against the writing. The server enforces the same
// rule in SQL; this module simply has no call that could break it.
//
// Titles and subtitles are sealed with the DEK before they leave the browser, exactly as entry text
// is — "10 Days of Darkness" says as much about someone as the pages inside it. The cover id travels
// in the clear: it names a design in the catalog this app ships.

import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from './client';
import { openWithKey, sealWithKey } from '../crypto/crypto-vault';
import { DEFAULT_COVER_ID } from '../journals/covers';

/** A decrypted journal, as the UI works with it. */
export interface Collection {
  id: string;
  title: string;
  subtitle: string;
  coverId: string;
  /** Membership, in shelf order. Ids only — the entries themselves live in the feed. */
  entryIds: string[];
  createdAt: Date;
  updatedAt: Date;
  sortIndex: number;
}

interface ServerCollection {
  id: string;
  title_cipher: string;
  subtitle_cipher: string | null;
  cover_id: string;
  sort_index: number;
  created_at: string;
  updated_at: string;
  entry_ids?: string[];
}

function parseDate(raw: string | null | undefined, fallback: Date): Date {
  if (!raw) return fallback;
  const iso = new Date(raw);
  if (!Number.isNaN(iso.getTime())) return iso;
  const sqlite = new Date(`${raw.replace(' ', 'T')}Z`);
  return Number.isNaN(sqlite.getTime()) ? fallback : sqlite;
}

async function open(dek: CryptoKey, cipher: string | null | undefined): Promise<string> {
  if (!cipher) return '';
  try {
    return await openWithKey(dek, cipher);
  } catch {
    // A box we cannot open is the wrong key or a corrupt row. An untitled journal on the shelf is
    // recoverable; a shelf that refuses to render is not.
    return '';
  }
}

async function decrypt(row: ServerCollection, dek: CryptoKey): Promise<Collection> {
  const createdAt = parseDate(row.created_at, new Date());
  return {
    id: row.id,
    title: await open(dek, row.title_cipher),
    subtitle: await open(dek, row.subtitle_cipher),
    coverId: row.cover_id || DEFAULT_COVER_ID,
    entryIds: row.entry_ids ?? [],
    createdAt,
    updatedAt: parseDate(row.updated_at, createdAt),
    sortIndex: row.sort_index ?? 0,
  };
}

export async function fetchCollections(dek: CryptoKey, signal?: AbortSignal): Promise<Collection[]> {
  const res = await apiGet<{ collections?: ServerCollection[] }>('/api/collections', signal);
  return Promise.all((res.collections ?? []).map((row) => decrypt(row, dek)));
}

export interface CollectionDraft {
  title: string;
  subtitle?: string;
  coverId?: string;
}

export async function createCollection(
  dek: CryptoKey,
  draft: CollectionDraft,
): Promise<Collection> {
  const id = crypto.randomUUID();
  const res = await apiPost<{ collection: ServerCollection }>('/api/collections', {
    id,
    title_cipher: await sealWithKey(dek, draft.title),
    subtitle_cipher: draft.subtitle?.trim() ? await sealWithKey(dek, draft.subtitle.trim()) : null,
    cover_id: draft.coverId ?? DEFAULT_COVER_ID,
  });
  return decrypt(res.collection, dek);
}

export interface CollectionPatch {
  title?: string;
  subtitle?: string;
  coverId?: string;
  sortIndex?: number;
}

/** Only what changed is sent, so an edit sheet never overwrites a field it did not show. */
export async function updateCollection(
  dek: CryptoKey,
  id: string,
  patch: CollectionPatch,
): Promise<Collection> {
  const body: Record<string, unknown> = {};
  if (patch.title !== undefined) body.title_cipher = await sealWithKey(dek, patch.title);
  if (patch.subtitle !== undefined) {
    body.subtitle_cipher = patch.subtitle.trim()
      ? await sealWithKey(dek, patch.subtitle.trim())
      : null;
  }
  if (patch.coverId !== undefined) body.cover_id = patch.coverId;
  if (patch.sortIndex !== undefined) body.sort_index = patch.sortIndex;

  const res = await apiPatch<{ collection: ServerCollection }>(
    `/api/collections/${encodeURIComponent(id)}`,
    body,
  );
  return decrypt(res.collection, dek);
}

/** Deletes the journal and its memberships. Every entry it gathered stays in the feed. */
export async function deleteCollection(id: string): Promise<void> {
  await apiDelete(`/api/collections/${encodeURIComponent(id)}`);
}

/**
 * The whole membership set, which is what the add-entries sheet produces when it closes. The server
 * diffs it, so sending the same list twice changes nothing and a retry is safe.
 */
export async function setCollectionEntries(id: string, entryIds: string[]): Promise<string[]> {
  const res = await apiPut<{ entry_ids?: string[] }>(
    `/api/collections/${encodeURIComponent(id)}/entries`,
    { entry_ids: entryIds },
  );
  return res.entry_ids ?? [];
}

/** Takes one entry out of one journal. The entry is untouched. */
export async function removeCollectionEntry(id: string, entryId: string): Promise<void> {
  await apiDelete(
    `/api/collections/${encodeURIComponent(id)}/entries/${encodeURIComponent(entryId)}`,
  );
}
